import theme from '@app/ui/theme'
import styled, { css } from 'styled-components'

// What every control on these forms is built from, so a date picker and a
// button that opens a modal end up the same size and shape as the inputs
// they sit between.
export const fieldInputStyles = css`
  height: 36px;
  border: none;
  border: 1px solid ${theme.colors.dark1};
  border-radius: ${theme.borderRadius}px;
  outline: none;
  text-align: center;
  font-size: 16px;
  line-height: 36px;
  font-weight: bold;
  font-family: 'Proxima Nova';
  background-color: ${theme.colors.white};
  box-sizing: border-box;
  align-self: flex-end;
  -webkit-appearance: none;
  appearance: none;
  opacity: 1;

  &::placeholder {
    font-weight: 400;
    color: #a5a5a5;
  }
`

interface FieldRowProps {
  justifyInnerContent?: string
  inputWidth?: string
  hasError?: boolean
}

// The row the preparation page is built from: the label on the left, the
// control on the right, one field to a line. It was written for the course
// information card and moved here when the slope table form took the same
// shape, so that the two forms cannot drift apart.
export const FieldRow = styled.div<FieldRowProps>`
  user-select: none;
  display: flex;
  flex: 1;
  justify-content: flex-start;
  z-index: 999;
  width: 100%;

  & > div {
    display: flex;
    flex: 1;
    justify-content: ${(props) => props.justifyInnerContent ?? 'space-between'};
    /* The label and its description sit against the top of the row rather
       than its middle, so a field whose control is taller than a line - the
       tees - does not leave its label floating halfway down. */
    align-items: flex-start;
    margin-top: 10px;
    margin-bottom: 0px;
  }

  & label {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  & label > span:nth-child(2) {
    color: #555;
    font-size: 10px;
  }

  & span {
    font-size: 15px;
    font-weight: 460;
    color: rgba(0, 0, 0, 0.8);
    float: left;
    line-height: 24px;
  }

  & input {
    ${fieldInputStyles}
    width: ${(props) => props.inputWidth ?? '45px'};
    border-color: ${(props) =>
      props.hasError ? theme.colors.warning : theme.colors.dark1};
  }

  & textarea {
    width: ${(props) => props.inputWidth ?? '45px'};
    border: none;
    border: 1px solid ${theme.colors.dark1};
    border-radius: ${theme.borderRadius}px;
    outline: none;
    font-size: 16px;
    line-height: 36px;
    font-weight: bold;
    font-family: 'Proxima Nova';
    box-sizing: border-box;
    align-self: flex-end;
    -webkit-appearance: none;
    appearance: none;
    opacity: 1;
  }

  & select {
    width: ${(props) => props.inputWidth ?? '45px'};
    height: 40px;
    border: none;
    border: 1px solid ${theme.colors.dark1};
    border-radius: ${theme.borderRadius}px;
    outline: none;
    text-align: center;
    font-size: 16px;
    line-height: 36px;
    font-weight: bold;
    font-family: 'Proxima Nova';
    box-sizing: border-box;
    align-self: flex-end;
    opacity: 1;
  }

  & select > option {
    flex: 1;
  }
`

// Holds the label against the icon that says whether the field is filled in,
// so a glance down the form finds what is still missing.
export const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const FieldControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

export const ControlRow = styled.div<{ width?: string }>`
  display: flex;
  align-items: center;
  width: ${(props) => props.width ?? 'auto'};
`

// A div rather than a span: the row styles float every span left, which would
// put the message under the label instead of under the field it belongs to.
export const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  text-align: right;
  color: ${theme.colors.warning};
`

// A field that opens something instead of taking typing, dressed as the
// inputs beside it so the column reads as one set of fields.
export const FieldButton = styled.button<{ hasError?: boolean }>`
  ${fieldInputStyles}
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Room for the icon on the right, matched on the left so the value stays
     centred the way the other fields are. The same inset as the date field. */
  padding: 0 32px;
  color: ${theme.colors.black};
  border-color: ${(props) =>
    props.hasError ? theme.colors.warning : theme.colors.dark1};
  cursor: pointer;

  &:hover {
    border-color: ${(props) =>
      props.hasError ? theme.colors.warning : theme.colors.secondary2};
  }
`

// Sits at the right edge of the field rather than beside the value, so it
// lines up with the calendar icon on the date field above it.
export const FieldButtonIcon = styled.span`
  position: absolute;
  top: 0;
  right: 14px;
  display: flex;
  align-items: center;
  height: 36px;
  pointer-events: none;
`

export const CheckContainer = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${theme.colors.secondary2};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`
